// Morrow — Comms tab.
//
// The messaging front door: each person connects their OWN ports (WhatsApp,
// Telegram, Slack, Discord) so Morrow can hear them and reach them where they
// already talk. Styled to match the Connections tab (SettingsSection +
// SettingsRow intro, card-glass rows, the white brand-logo tile, the shared
// pill/button classes) so it reads as one system.
//
//   GET  /api/morrow/comms/status?userId=…     → per-channel configured/links
//   POST /api/morrow/comms/whatsapp/start      → send a connect code to a number
//   POST /api/morrow/comms/verify              → claim the code typed back
//   POST /api/morrow/comms/telegram/start      → mint a t.me deep link
//   POST /api/morrow/comms/test                → send a test message to a port
//   POST /api/morrow/comms/disconnect          → revoke a link
//
// WhatsApp connect: number in → code arrives on WhatsApp → typed back here.
// If the cold send is blocked by Meta's 24h rule the server answers with a
// wa.me deep link instead; the user sends US the code and the inbound webhook
// links their number — the card polls status until it flips to connected.
// Telegram connect: pure deep link, the tap is the proof, poll until linked.
// Slack/Discord: send-ready server-side; connect UX ships next.

import { CheckIcon, Loader2Icon, MessagesSquareIcon, Trash2Icon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { apiUrl } from "../../apiBase";
import { useMorrowSignal } from "../../hooks/useMorrowSignal";
import { resolveComposioUserId } from "../../composioConnect";
import { cn } from "../../lib/utils";
import { useMorrowClerkAuth } from "../clerk/morrowClerkAuth";
import { QRCodeSvg } from "../ui/qr-code";
import { SettingsPageContainer, SettingsRow, SettingsSection } from "./settingsLayout";
import { useI18n, type MessageKey } from "~/i18n";

type CommsChannel = "whatsapp" | "telegram" | "slack" | "discord";

type CommsLink = { peerId: string; display: string; createdAt: string };

type CommsChannelStatus = {
  channel: CommsChannel;
  configured: boolean;
  connected: boolean;
  links: CommsLink[];
};

type ConnectKind = "number" | "deeplink" | "install";

const CHANNEL_META: Record<
  CommsChannel,
  {
    labelKey: MessageKey;
    logoSlug: string;
    taglineKey: MessageKey;
    pitchKey: MessageKey;
    capabilityKeys: readonly MessageKey[];
    connect: ConnectKind;
    accent: string;
  }
> = {
  whatsapp: {
    labelKey: "settings.comms.whatsapp.label",
    logoSlug: "whatsapp",
    taglineKey: "settings.comms.whatsapp.tagline",
    pitchKey: "settings.comms.whatsapp.pitch",
    capabilityKeys: [
      "settings.comms.whatsapp.cap.answers",
      "settings.comms.whatsapp.cap.voice",
      "settings.comms.whatsapp.cap.briefings",
    ],
    connect: "number",
    accent: "#25D366",
  },
  telegram: {
    labelKey: "settings.comms.telegram.label",
    logoSlug: "telegram",
    taglineKey: "settings.comms.telegram.tagline",
    pitchKey: "settings.comms.telegram.pitch",
    capabilityKeys: [
      "settings.comms.telegram.cap.connect",
      "settings.comms.telegram.cap.forward",
      "settings.comms.telegram.cap.replies",
    ],
    connect: "deeplink",
    accent: "#229ED9",
  },
  slack: {
    labelKey: "settings.comms.slack.label",
    logoSlug: "slack",
    taglineKey: "settings.comms.slack.tagline",
    pitchKey: "settings.comms.slack.pitch",
    capabilityKeys: [
      "settings.comms.slack.cap.add",
      "settings.comms.slack.cap.mention",
      "settings.comms.slack.cap.dm",
    ],
    connect: "install",
    accent: "#611f69",
  },
  discord: {
    labelKey: "settings.comms.discord.label",
    logoSlug: "discord",
    taglineKey: "settings.comms.discord.tagline",
    pitchKey: "settings.comms.discord.pitch",
    capabilityKeys: [
      "settings.comms.discord.cap.invite",
      "settings.comms.discord.cap.mention",
      "settings.comms.discord.cap.dm",
    ],
    connect: "install",
    accent: "#5865F2",
  },
};

// Shared class strings lifted from ConnectionsSettings so the two tabs match.
const PRIMARY_BUTTON =
  "inline-flex items-center justify-center gap-1.5 rounded-md border border-border bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring";
const GHOST_BUTTON =
  "inline-flex items-center justify-center gap-1.5 rounded-md border border-border/60 px-3 py-1 text-xs font-medium text-muted-foreground transition hover:bg-muted/50 hover:text-foreground disabled:opacity-60 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring";

async function fetchStatus(userId: string): Promise<CommsChannelStatus[] | null> {
  try {
    const res = await fetch(
      apiUrl(`/api/morrow/comms/status?userId=${encodeURIComponent(userId)}`),
      { headers: { accept: "application/json" } },
    );
    if (!res.ok) return null;
    const data = (await res.json()) as { channels?: CommsChannelStatus[] };
    return Array.isArray(data.channels) ? data.channels : null;
  } catch {
    return null;
  }
}

async function postJson<T>(path: string, body: Record<string, unknown>): Promise<T | null> {
  try {
    const res = await fetch(apiUrl(path), {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify(body),
    });
    return (await res.json().catch(() => null)) as T | null;
  } catch {
    return null;
  }
}

/** The brand-logo tile — real logo on a white square (crisp in both themes),
 * with a monogram fallback if the image fails. Mirrors ConnectionsSettings. */
function ChannelLogo({ slug, label, accent }: { slug: string; label: string; accent: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <span
        className="flex size-11 shrink-0 items-center justify-center rounded-xl text-sm font-semibold uppercase text-white"
        style={{ backgroundColor: accent }}
      >
        {label.slice(0, 2)}
      </span>
    );
  }
  return (
    <div className="flex size-11 shrink-0 items-center justify-center rounded-xl border border-border bg-white dark:bg-white">
      <img
        src={`https://logos.composio.dev/api/${slug}`}
        alt=""
        aria-hidden="true"
        className="size-6 object-contain"
        onError={() => setFailed(true)}
      />
    </div>
  );
}

function WhatsappConnectFlow({ userId }: { userId: string }) {
  const { t } = useI18n();
  const [state, setState] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [waLink, setWaLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const start = useCallback(async () => {
    setState("loading");
    setError(null);
    const result = await postJson<{ waLink?: string; error?: string }>(
      "/api/morrow/comms/whatsapp/qr",
      { userId },
    );
    if (result?.waLink) {
      setWaLink(result.waLink);
      setState("ready");
    } else {
      setError(result?.error ?? t("settings.comms.serverError"));
      setState("error");
    }
  }, [t, userId]);

  if (state === "ready" && waLink) {
    return (
      <div className="flex flex-col items-center gap-3">
        <p className="text-center text-[13px] leading-[1.45] text-muted-foreground/80">
          {t("settings.comms.whatsapp.qrReadyHint")}
        </p>
        <div className="rounded-xl border border-border bg-white p-3">
          <QRCodeSvg value={waLink} size={156} title={t("settings.comms.whatsapp.qrTitle")} />
        </div>
        <a href={waLink} target="_blank" rel="noreferrer" className={PRIMARY_BUTTON}>
          {t("settings.comms.whatsapp.openWhatsApp")}
        </a>
        <span className="text-xs text-muted-foreground/70">
          {t("settings.comms.whatsapp.waitingMessage")}
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <p
        className={cn(
          "text-[13px] leading-[1.45]",
          state === "error" ? "text-destructive" : "text-muted-foreground/80",
        )}
      >
        {state === "error" ? error : t("settings.comms.whatsapp.scanHint")}
      </p>
      <button
        type="button"
        className={PRIMARY_BUTTON}
        onClick={() => void start()}
        disabled={state === "loading"}
      >
        {state === "loading" ? (
          <Loader2Icon className="size-3.5 animate-spin" />
        ) : (
          t("settings.comms.whatsapp.connect")
        )}
      </button>
    </div>
  );
}

/** Slack / Discord click-to-add: one button that opens the provider's install
 * consent, then the OAuth callback links you and the panel poll picks it up. */
function InstallConnectFlow({
  userId,
  channel,
  label,
}: {
  userId: string;
  channel: CommsChannel;
  label: string;
}) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = useCallback(async () => {
    setBusy(true);
    setError(null);
    const result = await postJson<{ url?: string; error?: string }>("/api/morrow/comms/install", {
      userId,
      channel,
    });
    setBusy(false);
    if (result?.url) {
      setPending(true);
      window.open(result.url, "_blank", "noopener");
    } else {
      setError(result?.error ?? t("settings.comms.serverError"));
    }
  }, [channel, t, userId]);

  return (
    <div className="flex flex-col gap-2">
      {error ? <p className="text-[13px] leading-[1.45] text-destructive">{error}</p> : null}
      {pending && !error ? (
        <p className="text-[13px] leading-[1.45] text-muted-foreground/80">
          {t("settings.comms.install.approveHint", { label })}
        </p>
      ) : null}
      <button type="button" className={PRIMARY_BUTTON} onClick={() => void start()} disabled={busy}>
        {busy ? (
          <Loader2Icon className="size-3.5 animate-spin" />
        ) : (
          t("settings.comms.addTo", { label })
        )}
      </button>
    </div>
  );
}

function TelegramConnectFlow({ userId }: { userId: string }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = useCallback(async () => {
    setBusy(true);
    setError(null);
    const result = await postJson<{ link?: string; error?: string }>(
      "/api/morrow/comms/telegram/start",
      { userId },
    );
    setBusy(false);
    if (result?.link) {
      setPending(true);
      window.open(result.link, "_blank", "noopener");
    } else {
      setError(result?.error ?? t("settings.comms.serverError"));
    }
  }, [t, userId]);

  return (
    <div className="flex flex-col gap-2">
      <p
        className={cn(
          "text-[13px] leading-[1.45]",
          error ? "text-destructive" : "text-muted-foreground/80",
        )}
      >
        {error
          ? error
          : pending
            ? t("settings.comms.telegram.pending")
            : t("settings.comms.telegram.hint")}
      </p>
      <button type="button" className={PRIMARY_BUTTON} onClick={() => void start()} disabled={busy}>
        {busy ? (
          <Loader2Icon className="size-3.5 animate-spin" />
        ) : (
          t("settings.comms.telegram.connect")
        )}
      </button>
    </div>
  );
}

function ChannelRow({
  status,
  userId,
  onChanged,
}: {
  status: CommsChannelStatus;
  userId: string;
  onChanged: () => void;
}) {
  const { t } = useI18n();
  const meta = CHANNEL_META[status.channel];
  const label = t(meta.labelKey);
  const [testState, setTestState] = useState<"idle" | "busy" | "sent" | "failed">("idle");

  const sendTest = useCallback(async () => {
    setTestState("busy");
    const result = await postJson<{ sent?: boolean }>("/api/morrow/comms/test", {
      userId,
      channel: status.channel,
    });
    setTestState(result?.sent ? "sent" : "failed");
    setTimeout(() => setTestState("idle"), 3000);
  }, [status.channel, userId]);

  const disconnect = useCallback(
    async (peerId: string) => {
      await postJson("/api/morrow/comms/disconnect", { userId, channel: status.channel, peerId });
      onChanged();
    },
    [onChanged, status.channel, userId],
  );

  return (
    <div className="card-glass flex flex-col gap-3.5 rounded-xl border border-transparent p-4">
      <div className="flex items-start gap-3">
        <ChannelLogo slug={meta.logoSlug} label={label} accent={meta.accent} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold tracking-[-0.01em] text-foreground">{label}</h3>
            {status.connected ? (
              <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 text-[11px] font-medium text-emerald-500">
                <CheckIcon className="size-3" aria-hidden="true" />
                {t("settings.comms.connected")}
              </span>
            ) : null}
          </div>
          <p className="text-[13px] font-medium leading-[1.4] text-foreground/80">
            {t(meta.taglineKey)}
          </p>
        </div>
      </div>

      <p className="text-[13px] leading-[1.5] text-muted-foreground/85">{t(meta.pitchKey)}</p>

      <div className="flex flex-wrap gap-1.5">
        {meta.capabilityKeys.map((capKey) => (
          <span
            key={capKey}
            className="inline-flex items-center rounded-full border border-border/60 bg-muted/40 px-2 py-0.5 text-[11px] font-medium text-muted-foreground"
          >
            {t(capKey)}
          </span>
        ))}
      </div>

      <div className="mt-0.5 border-t border-border/50 pt-3.5">
        {status.links.length > 0 ? (
          <div className="flex flex-col gap-1.5">
            {status.links.map((link) => (
              <div
                key={link.peerId}
                className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-background/60 px-3 py-1.5"
              >
                <span className="truncate text-[13px] text-foreground">
                  {link.display || link.peerId}
                </span>
                <button
                  type="button"
                  aria-label={t("settings.comms.disconnectAria", { name: label })}
                  onClick={() => void disconnect(link.peerId)}
                  className="inline-flex size-6 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Trash2Icon className="size-3.5" />
                </button>
              </div>
            ))}
            <div className="flex pt-0.5">
              <button type="button" className={GHOST_BUTTON} onClick={() => void sendTest()}>
                {testState === "busy" ? (
                  <Loader2Icon className="size-3 animate-spin" />
                ) : testState === "sent" ? (
                  t("settings.comms.testSent")
                ) : testState === "failed" ? (
                  t("settings.comms.testFailed")
                ) : (
                  t("settings.comms.testSend")
                )}
              </button>
            </div>
          </div>
        ) : status.configured ? (
          meta.connect === "number" ? (
            <WhatsappConnectFlow userId={userId} />
          ) : meta.connect === "deeplink" ? (
            <TelegramConnectFlow userId={userId} />
          ) : (
            <InstallConnectFlow userId={userId} channel={status.channel} label={label} />
          )
        ) : (
          <div className="flex items-center gap-2">
            <button type="button" className={PRIMARY_BUTTON} disabled>
              {meta.connect === "install"
                ? t("settings.comms.addTo", { label })
                : t("settings.comms.connect")}
            </button>
            <span className="text-xs text-muted-foreground/70">
              {t("settings.comms.comingSoon")}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export function CommsSettings() {
  const { t } = useI18n();
  const {
    isLoaded: isClerkLoaded,
    isSignedIn,
    userId: clerkUserId,
    openSignIn,
  } = useMorrowClerkAuth();
  // Connections key to the VERIFIED Clerk identity (server ignores client ids),
  // so connecting requires sign-in — else a link would attach to a throwaway
  // guest id and "disappear" later.
  const signedIn = isClerkLoaded && isSignedIn;
  const userId = useMemo(
    () => resolveComposioUserId(signedIn ? clerkUserId : null),
    [signedIn, clerkUserId],
  );

  const [channels, setChannels] = useState<CommsChannelStatus[] | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refresh = useCallback(() => {
    if (!signedIn) return;
    if (typeof document !== "undefined" && document.hidden) return;
    void fetchStatus(userId).then((next) => {
      if (next) setChannels(next);
    });
  }, [signedIn, userId]);

  useEffect(() => {
    if (!signedIn) return;
    refresh();
    // Poll while the tab is open: deep-link connects (Telegram, the wa.me
    // fallback) complete out-of-band via webhooks, so the panel discovers them
    // by re-reading status. 20s is plenty — connects aren't sub-second.
    pollRef.current = setInterval(refresh, 90000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [signedIn, refresh]);
  // Instant refresh when the server pokes that this user's comms status changed
  // (a link completed out-of-band via webhook / personal-WhatsApp connect).
  useMorrowSignal("comms", () => refresh());

  return (
    <SettingsPageContainer>
      <SettingsSection
        id="comms"
        title={t("settings.section.comms")}
        icon={<MessagesSquareIcon className="size-4.5" />}
      >
        <SettingsRow
          title={t("settings.comms.channelsTitle")}
          description={t("settings.comms.channelsDescription")}
        />
        {isClerkLoaded && !isSignedIn ? (
          <div className="px-3 sm:px-4">
            <div className="card-glass flex flex-col items-center gap-3 rounded-xl border border-transparent p-6 text-center">
              <MessagesSquareIcon className="size-6 text-muted-foreground" />
              <p className="max-w-sm text-sm text-muted-foreground">
                {t("settings.comms.signInBody")}
              </p>
              <button type="button" className={PRIMARY_BUTTON} onClick={() => void openSignIn?.()}>
                {t("settings.comms.signInCta")}
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 px-3 sm:px-4 lg:grid-cols-2">
            {(channels ?? []).map((status) => (
              <ChannelRow
                key={status.channel}
                status={status}
                userId={userId}
                onChanged={refresh}
              />
            ))}
            {channels === null ? (
              <div className="col-span-full flex items-center justify-center py-10 text-muted-foreground">
                <Loader2Icon className="size-5 animate-spin" />
              </div>
            ) : null}
          </div>
        )}
      </SettingsSection>
    </SettingsPageContainer>
  );
}
